import { ProactiveModalContent } from "~/types/chatbot";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * Internal generic deep merge over plain-object shapes. Operates on
 * `Record<string, unknown>` so that recursive calls don't need to falsely cast
 * nested sub-objects (like `heading`, `cta`) as `ProactiveModalContent`.
 *
 * Arrays and non-plain objects are replaced wholesale when override is defined;
 * `undefined` on the override means "use base".
 */
const deepMergePlainObjects = (
  base: Record<string, unknown> | undefined,
  override: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (override === undefined) return base;
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override;
  }

  const merged: Record<string, unknown> = { ...base };

  Object.keys(override).forEach((key) => {
    const overrideValue = override[key];
    if (overrideValue === undefined) return;

    const baseValue = base[key];
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      merged[key] = deepMergePlainObjects(baseValue, overrideValue);
      return;
    }

    merged[key] = overrideValue;
  });

  return merged;
};

/**
 * Deep merge per-trigger proactive modal copy overrides on top of the base.
 *
 * Ported from internet.frontier's FRONT-12769 implementation so per-trigger
 * overrides only declare the fields that differ from the base. Only the
 * outermost call asserts the shape as `ProactiveModalContent`; recursion is
 * shape-agnostic.
 */
export const mergeProactiveModalContent = (
  base: ProactiveModalContent | undefined,
  override: ProactiveModalContent | undefined
): ProactiveModalContent | undefined => {
  const merged = deepMergePlainObjects(
    base as Record<string, unknown> | undefined,
    override as Record<string, unknown> | undefined
  );
  return merged as ProactiveModalContent | undefined;
};
